<template>
  <div class="inline-form">
    <div class="inline-form-title">Cadastrar convênio</div>
    <div class="loading loading-lg" v-if="loading"/>
    <template v-else>
      <div class="columns">
        <div
          class="column col-5 col-sm-12 form-group"
          :class="{ 'has-error': $v.form.insuranceId.$error }"
        >
          <label class="form-label">Operadora</label>
          <select
            class="form-select"
            v-model="form.insuranceId"
            @change="$v.form.insuranceId.$touch()"
          >
            <option value="">[Selecione]</option>
            <option value="is_new">Cadastrar nova operadora</option>
            <option v-for="ins in insuranceOptions" :value="ins.id" :key="ins.id">
              {{ ins.name }}
            </option>
          </select>
          <div
            class="form-input-hint"
            v-if="$v.form.insuranceId.$error && !$v.form.insuranceId.required"
          >Campo obrigatório</div>
        </div>
        <div
          class="column col-7 col-sm-12 form-group"
          v-if="form.insuranceId === 'is_new'"
          :class="{ 'has-error': $v.form.newInsuranceName.$error }"
        >
          <label class="form-label">Nome da operadora</label>
          <input
            ref="insuranceName"
            type="text"
            class="form-input"
            v-model="form.newInsuranceName"
            placeholder="Nome da operadora"
            autocomplete="nope"
            @blur="$v.form.newInsuranceName.$touch()"
          >
          <div
            class="form-input-hint"
            v-if="$v.form.newInsuranceName.$error && !$v.form.newInsuranceName.required"
          >Campo obrigatório</div>
        </div>
        <div
          class="column col-sm-12 form-group"
          :class="[form.insuranceId === 'is_new' ? 'col-8' : 'col-5',
            { 'has-error': $v.form.name.$error }]"
        >
          <label class="form-label">Plano</label>
          <input
            ref="planName"
            type="text"
            class="form-input"
            v-model="form.name"
            placeholder="Nome do plano ou particular"
            autocomplete="nope"
            @blur="$v.form.name.$touch()"
          >
          <div class="form-input-hint" v-if="$v.form.name.$error && !$v.form.name.required">
            Campo obrigatório
          </div>
        </div>
        <div
          class="column col-sm-12 form-group"
          :class="[form.insuranceId === 'is_new' ? 'col-4' : 'col-2',
            { 'has-error': $v.form.type.$error }]"
        >
          <label class="form-label">Tipo</label>
          <select class="form-select" v-model="form.type" @change="$v.form.type.$touch()">
            <option value="">[Selecione]</option>
            <option v-for="(label, key) in insuranceTypes" :value="key" :key="key">
              {{ label }}
            </option>
          </select>
          <div class="form-input-hint" v-if="$v.form.type.$error && !$v.form.type.required">
            Campo obrigatório
          </div>
        </div>
      </div>

      <div class="inline-note">
        <fa-icon :icon="['fal', 'info-circle']"/>
        <span>
          Será criada a tabela de preços <strong>“Valores {{ tableLabel }}”</strong>,
          vinculada a procedimentos, exames, materiais e medicamentos deste plano.
          <template v-if="form.type === 'insurance'">
            O registro ANS fica como <strong>0</strong> até você preencher no cadastro do convênio.
          </template>
        </span>
      </div>

      <div class="inline-form-actions">
        <button class="btn btn-gray" :disabled="saving" @click="$emit('cancel')">Fechar</button>
        <button
          class="btn btn-secondary btn-icon btn-icon-left"
          :class="{ loading: saving }"
          :disabled="saving"
          @click="save"
        ><fa-icon :icon="['fal', 'save']"/>Adicionar</button>
      </div>
    </template>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import escapeRegExp from 'lodash/escapeRegExp';
import { customName } from '@/helpers/insurance';
import { types as insuranceTypes } from '@/data/insurance-types';

export default {
  props: {
    initialQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      insurances: [],
      loading: false,
      saving: false,
      insuranceTypes,
      form: {
        insuranceId: '',
        newInsuranceName: '',
        name: '',
        type: '',
      },
    };
  },
  computed: {
    insuranceOptions() {
      const seen = new Set();
      return this.insurances.filter(({ id }) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },
    tableLabel() {
      return this.tableLabelFor(this.insuranceLabel, this.form.name);
    },
    insuranceLabel() {
      if (this.form.insuranceId === 'is_new') return this.form.newInsuranceName || 'operadora';
      const found = this.insurances.find(({ id }) => id === this.form.insuranceId);
      return found ? found.name : 'operadora';
    },
  },
  validations() {
    return {
      form: {
        insuranceId: { required },
        newInsuranceName: this.form.insuranceId === 'is_new' ? { required } : {},
        name: { required },
        type: { required },
      },
    };
  },
  async mounted() {
    await this.loadInsurances();
    this.applyInitialQuery();
  },
  methods: {
    async loadInsurances() {
      this.loading = true;
      await this.$httpX
        .get('/insurances', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.insurances = data.items;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    applyInitialQuery() {
      const query = this.initialQuery.trim();
      const normalize = v => v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      const found = query
        ? this.insurances.find(({ name }) => normalize(query).includes(normalize(name)))
        : null;

      if (found) {
        this.form.insuranceId = found.id;
        this.form.name = query.replace(new RegExp(escapeRegExp(found.name), 'i'), '').trim();
      } else if (query) {
        this.form.insuranceId = 'is_new';
        this.form.newInsuranceName = query;
      }

      this.$nextTick(() => {
        const ref = this.form.insuranceId === 'is_new' && !this.form.newInsuranceName
          ? this.$refs.insuranceName
          : this.$refs.planName;
        if (ref) ref.focus();
      });
    },
    tableLabelFor(insuranceName, planName) {
      const insurance = String(insuranceName || '').trim();
      const plan = String(planName || '').trim();
      if (!plan) return insurance;
      return customName({ name: insurance, plan: { name: plan } });
    },
    findDuplicated() {
      const same = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
      const newInsurance = this.form.insuranceId === 'is_new';

      if (newInsurance) {
        const found = this.insurances.find(({ name }) => same(name, this.form.newInsuranceName));
        if (found) {
          return `Já existe uma operadora com o nome "${found.name}"`;
        }
        return null;
      }

      const found = this.insurances.find(({ id, plan }) => (
        id === this.form.insuranceId
        && plan && !plan.deletedAt
        && same(plan.name, this.form.name)
      ));
      if (found) {
        return `Já existe um plano com o nome "${found.plan.name}" nesta operadora`;
      }
      return null;
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      const duplicated = this.findDuplicated();
      if (duplicated) {
        this.$toast.show(duplicated, { type: 'error' });
        return;
      }

      this.saving = true;

      try {
        let { insuranceId } = this.form;
        let insuranceName = this.insuranceLabel;
        const newInsurance = insuranceId === 'is_new';

        if (newInsurance) {
          const { data } = await this.$httpX.post('/insurances', { name: this.form.newInsuranceName });
          insuranceId = data.id;
          insuranceName = data.name;
        }

        const payload = {
          name: this.form.name,
          type: this.form.type,
          active: true,
          createPriceTable: true,
        };

        if (this.form.type === 'insurance') {
          payload.record = '0';
        }

        const { data: plan } = await this.$httpX.post(`/insurances/${insuranceId}/plans`, payload);
        const table = plan.settings && plan.settings.general && plan.settings.general.table;

        this.$toast.show('Convênio cadastrado com sucesso');
        this.$emit('saved', {
          id: plan.id,
          planId: plan.id,
          insuranceId,
          insuranceName,
          planName: plan.name,
          planType: plan.type,
          tableId: table ? table.procedureId : null,
          tableName: `Valores ${this.tableLabelFor(insuranceName, plan.name)}`,
          dailyLimit: null,
          isNew: true,
          newInsurance,
        });
      } catch (e) {
        const message = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao salvar convênio';
        this.$toast.show(message, { type: 'error' });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
